/**
 * verooglTourScript — the 9-beat narration script for the VEROOGLE
 * client-selling demo tour.  Each step points at a target element via
 * its `data-testid` so the overlay can draw a spotlight + arrow around
 * the right spot.
 *
 * Phase A ships this script running on browser speechSynthesis.
 * Phase B swaps the `audio` field in each step for a pre-rendered
 * ElevenLabs MP3 URL once the user approves the copy and ships their
 * voice ID + API key.
 */

// The static mount is namespaced under /api so the K8s ingress routes
// it to the FastAPI backend (not the React dev server).  Audio URLs are
// built from REACT_APP_BACKEND_URL to work in dev + preview + prod.
const _BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const _url  = (step, slug = "veroogle") => `${_BASE}/api/static/audio/${slug}-step-${step}.mp3`;

export const tourAudioUrl = (slug, step, version) =>
  version ? `${_url(step, slug)}?v=${encodeURIComponent(version)}` : _url(step, slug);

export const VEROOGLE_TOUR_SCRIPT = [
  {
    id: "savings",
    target: "veroogle-metric-savings",
    kicker: "// 01 · NEXENGINES ANNUAL SAVINGS",
    title: "What Nexengines saves you",
    caption:
      "This number right here is what Nexengines saves VEROOGLE every year. It's built from your friction audit: the hours your team loses to manual work, plus the tools we replace. And it keeps climbing as we work leads live.",
    step: "01",
    audio: _url("01"),
  },
  {
    id: "scraped",
    target: "veroogle-metric-scraped",
    kicker: "// 02 · LEADS SCRAPED",
    title: "One click. Full prospect list.",
    caption:
      "This tile counts every prospect pulled from Google Places and local permit filings. One click runs a geo-filtered sweep across your target vertical.",
    step: "02",
    audio: _url("02"),
  },
  {
    id: "hot",
    target: "veroogle-metric-hot",
    kicker: "// 03 · HOT LEADS",
    title: "Ready to call today",
    caption:
      "Hot leads are the ones scoring seventy or higher — ready to call today. The number updates the moment you tune the weights to match your appetite.",
    step: "03",
    audio: _url("03"),
  },
  {
    id: "pipeline",
    target: "veroogle-metric-pipeline",
    kicker: "// 04 · PIPELINE VOLUME",
    title: "The revenue in motion",
    caption:
      "Pipeline volume rolls up the monthly card volume of every merchant you've moved into VEROOGLE's acquisition flow. That's the revenue in motion.",
    step: "04",
    audio: _url("04"),
  },
  {
    id: "recap-metrics",
    type: "summary",
    kicker: "// RECAP · THE FOUR NUMBERS",
    title: "What you just saw",
    bullets: [
      "What Nexengines saves you every year",
      "Prospects scraped in one click",
      "Hot leads ready to call today",
      "Card volume moving through the pipeline",
    ],
    caption:
      "Quick recap. Those four numbers up top tell the whole story: what Nexengines saves you every year, how many prospects we've pulled, which ones are hot enough to call today, and the card volume already moving through your pipeline. Now let's look at the tools that drive them.",
    step: "S1",
    audio: _url("S1"),
  },
  {
    id: "phase1",
    target: "nds-accordion-phase1_veroogle",
    action: "open-phase1",
    kicker: "// 05 · PHASE 1 — LEAD INTELLIGENCE",
    title: "Where the money gets made",
    caption:
      "Phase 1 is where the money gets made. Scraping, scoring, outreach, and a merchant statement analyzer that shows the client their savings in under a minute. All four tools are live right now.",
    step: "05",
    audio: _url("05"),
  },
  {
    id: "tool-scrape",
    target: "veroogle-card-t1010cr",
    kicker: "// PHASE 1 · TOOL 1 · T1010CR",
    title: "Lead Scraping & Discovery",
    caption:
      "First, Lead Scraping and Discovery. Pick a vertical and a city, and VEROOGLE pulls every matching merchant in seconds: address, estimated card volume, permits and a direct phone number. Watch. We just found a fresh batch of restaurants in Columbus and pushed the best ones straight into scoring.",
    action: "scrape",
    step: "P1",
    audio: _url("P1"),
  },
  {
    id: "tool-score",
    target: "veroogle-card-t1020cr",
    kicker: "// PHASE 1 · TOOL 2 · T1020CR",
    title: "Lead Scoring & Intake Matrix",
    caption:
      "Next, the Lead Scoring and Intake Matrix. Every merchant is scored against your appetite: volume, vertical risk, and how long they've been in business. Hot leads float to the top, so your reps only call the ones worth calling. We'll send the top prospects into the pipeline.",
    action: "score",
    step: "P2",
    audio: _url("P2"),
  },
  {
    id: "tool-pipeline",
    target: "veroogle-card-t1030cr",
    kicker: "// PHASE 1 · TOOL 3 · T1030CR",
    title: "Lead Pipeline & Automatic Outreach",
    caption:
      "Here's the Lead Pipeline with Automatic Outreach. Each merchant moves from scraped, to contacted, to statement in hand, all the way to won. And every stage fires its own text, email and voice touch automatically. Watch this lead advance, and the next outreach goes out on its own.",
    action: "pipeline",
    step: "P3",
    audio: _url("P3"),
  },
  {
    id: "tool-statement",
    target: "veroogle-card-t1040cr",
    kicker: "// PHASE 1 · TOOL 4 · T1040CR",
    title: "Statement Review & Rate Analysis",
    caption:
      "Finally, Statement Review and Rate Analysis. Drop in a merchant's processing statement and VEROOGLE breaks down every line, spots the hidden markups, and shows exactly how much they'd save switching to you. That's the number that closes the deal.",
    action: "statement",
    step: "P4",
    audio: _url("P4"),
  },
  {
    id: "recap-phase1",
    type: "summary",
    kicker: "// RECAP · PHASE 1",
    title: "Your front door, in a nutshell",
    bullets: [
      "Find merchants by vertical and geography",
      "Score every lead against your appetite",
      "Work them through the pipeline",
      "Prove savings from their own statement",
    ],
    caption:
      "So, Phase 1 in a nutshell. We find the merchants, score them, work them through the pipeline, and prove their savings straight from their own statement. That's your front door. Next, let's see what happens once a merchant says yes.",
    step: "S2",
    audio: _url("S2"),
  },
  {
    id: "phase2",
    target: "nds-accordion-phase2_veroogle",
    action: "open-phase2",
    kicker: "// 06 · PHASE 2 — ONBOARDING & VERIFICATION",
    title: "Qualified lead to onboarded merchant",
    caption:
      "Phase 2 takes qualified leads straight into dynamic intake, document vault, KYB and KYC, and automated underwriting. Phase 1 hands off the moment a merchant says yes.",
    step: "06",
    audio: _url("06"),
  },
  {
    id: "tool-intake",
    target: "veroogle-card-t2500cr",
    kicker: "// PHASE 2 · TOOL 1 · T2500CR",
    title: "Dynamic Intake",
    caption:
      "Phase 2 starts the moment a merchant says yes. Dynamic Intake builds their application for them, pulling everything we already know from Phase 1: business details, owner profile and processing volume. No retyping, no back and forth. Watch it fill itself in.",
    action: "run-t2500",
    step: "Q1",
    audio: _url("Q1"),
  },
  {
    id: "tool-vault",
    target: "veroogle-card-t2220cr",
    kicker: "// PHASE 2 · TOOL 2 · T2220CR",
    title: "Document Vault",
    caption:
      "Next, the Document Vault. Licence, voided cheque, bank statements and incorporation papers land in one encrypted place, and every file is mirrored to your Google Drive automatically. No more chasing attachments over email.",
    action: "run-t2220",
    step: "Q2",
    audio: _url("Q2"),
  },
  {
    id: "tool-kyc",
    target: "veroogle-card-t2300cr",
    kicker: "// PHASE 2 · TOOL 3 · T2300CR",
    title: "Automated KYB / KYC Verification",
    caption:
      "Now verification runs on its own. Identity, TIN match, OFAC sanctions, Secretary of State and background checks, one after another, in seconds instead of days. Every check is logged for compliance, and the merchant is stamped verified.",
    action: "run-t2300",
    step: "Q3",
    audio: _url("Q3"),
  },
  {
    id: "tool-underwriting",
    target: "veroogle-card-t2400cr",
    kicker: "// PHASE 2 · TOOL 4 · T2400CR",
    title: "Automated Underwriting & Risk Scoring",
    caption:
      "Finally, underwriting. Your own rules score the merchant's risk, check their volume and chargeback history, and set an approved processing limit. Here's the decision: approved. That merchant is now live, and onboarded count ticks up.",
    action: "run-t2400",
    step: "Q4",
    audio: _url("Q4"),
  },
  {
    id: "recap-phase2",
    type: "summary",
    kicker: "// RECAP · PHASE 2",
    title: "Yes to live, without the paperwork",
    bullets: [
      "Application auto-filled from Phase 1 data",
      "Documents encrypted and mirrored to Drive",
      "KYB / KYC checks run in seconds",
      "Underwriting approves with your own rules",
    ],
    caption:
      "That's Phase 2. The application fills itself, documents are locked down and synced, verification runs in seconds, and underwriting approves with your rules. What used to take weeks now takes minutes. Next, let's see how VEROOGLE protects the book.",
    step: "S4",
    audio: _url("S4"),
  },
  {
    id: "phase3",
    target: "nds-accordion-phase3_veroogle",
    action: "open-phase3",
    kicker: "// 07 · PHASE 3 — PORTFOLIO RISK & DISPUTE OPS",
    title: "Keep your book healthy",
    caption:
      "Phase 3 is the risk engine — MID monitoring, fraud scoring, chargeback automation, and your multi-ISO residual splits. It keeps your book healthy once the deal is closed.",
    step: "07",
    audio: _url("07"),
  },
  {
    id: "tool-mid",
    target: "veroogle-card-t3100cr",
    kicker: "// PHASE 3 · TOOL 1 · T3100CR",
    title: "MID Monitoring & Volume Tracking",
    caption:
      "Phase 3 protects the book you just built. MID Monitoring watches every merchant's volume against their cap in real time. See that spike on day eleven? VEROOGLE catches it, fires a cap warning, and adjusts the limit before it ever becomes a problem.",
    action: "run-t3100",
    step: "R1",
    audio: _url("R1"),
  },
  {
    id: "tool-fraud",
    target: "veroogle-card-t3200cr",
    kicker: "// PHASE 3 · TOOL 2 · T3200CR",
    title: "Fraud Scoring & Anomaly Alerts",
    caption:
      "Next, Fraud Scoring. Every transaction gets a live risk score. Normal tickets flow through, but this one, a twenty-four hundred dollar card-not-present sale from another state, gets held for review and the owner is alerted by text, instantly.",
    action: "run-t3200",
    step: "R2",
    audio: _url("R2"),
  },
  {
    id: "tool-dispute",
    target: "veroogle-card-t3300cr",
    kicker: "// PHASE 3 · TOOL 3 · T3300CR",
    title: "Chargeback & Dispute Hub",
    caption:
      "When a chargeback does land, the Dispute Hub takes over. The alert comes in, the order is matched, the evidence pack builds itself, and representment is filed automatically. Result: won, and twelve hundred forty dollars recovered.",
    action: "run-t3300",
    step: "R3",
    audio: _url("R3"),
  },
  {
    id: "tool-residual",
    target: "veroogle-card-t4100cr",
    kicker: "// PHASE 3 · TOOL 4 · T4100CR",
    title: "Multi-Tier Residual Engine",
    caption:
      "And finally, the Residual Engine. This month's residuals split automatically across every tier: the ISO, the agent and the sub-agent, each with their own percentage. No spreadsheets, no disputes, just payouts scheduled.",
    action: "run-t4100",
    step: "R4",
    audio: _url("R4"),
  },
  {
    id: "recap-phase3",
    type: "summary",
    kicker: "// RECAP · PHASE 3",
    title: "Your book, protected around the clock",
    bullets: [
      "Volume caps watched and adjusted in real time",
      "Fraud scored on every transaction",
      "Chargebacks fought and won automatically",
      "Residuals split across every tier",
    ],
    caption:
      "That's Phase 3. Volume is watched around the clock, fraud is caught as it happens, chargebacks are fought and won for you, and every tier gets paid correctly. Your book stays healthy while your team focuses on growth.",
    step: "S5",
    audio: _url("S5"),
  },
  {
    id: "phase4",
    target: "nds-accordion-phase4_veroogle",
    action: "open-phase4",
    kicker: "// 08 · PHASE 4 — ACCOUNTING & TERMINAL LOGISTICS",
    title: "Run clean books. Ship hardware.",
    caption:
      "Phase 4 is the cash side — processor reconciliation, agent payouts, terminal management, and gateway routing. Everything you need to run clean books and ship hardware.",
    step: "08",
    audio: _url("08"),
  },
  {
    id: "tool-recon",
    target: "veroogle-card-t4200cr",
    kicker: "// PHASE 4 · TOOL 1 · T4200CR",
    title: "Processor Reconciliation & Auditing",
    caption:
      "Phase 4 is where the money gets counted, correctly. Reconciliation matches your buy-rate against the processor's clearing report, line by line. Here, it finds a hundred eighty-six dollar over-billing and files the correction automatically.",
    action: "run:veroogle-t4200-run",
    step: "T1",
    audio: _url("T1"),
  },
  {
    id: "tool-payouts",
    target: "veroogle-card-t4300cr",
    kicker: "// PHASE 4 · TOOL 2 · T4300CR",
    title: "Automated Payouts & Commission Reports",
    caption:
      "Next, payouts. The ACH batch stages itself, every agent gets a clean commission statement, you approve with one tap, and the batch is released. Payday without the spreadsheet marathon.",
    action: "run:veroogle-t4300-run",
    step: "T2",
    audio: _url("T2"),
  },
  {
    id: "tool-terminals",
    target: "veroogle-card-t5100cr",
    kicker: "// PHASE 4 · TOOL 3 · T5100CR",
    title: "Terminal Management & Deployment",
    caption:
      "Terminal Management handles the hardware. A Clover is picked from inventory, shipped with tracking, delivered, and activated on the merchant's MID, all tracked in one place.",
    action: "run:veroogle-t5100-run",
    step: "T3",
    audio: _url("T3"),
  },
  {
    id: "tool-routing",
    target: "veroogle-card-t5200cr",
    kicker: "// PHASE 4 · TOOL 4 · T5200CR",
    title: "Gateway & MID-Routing Integration",
    caption:
      "And Gateway Routing balances traffic across your MIDs. When one gets close to its cap, transactions are rerouted automatically, so the merchant never sees a decline.",
    action: "run:veroogle-t5200-run",
    step: "T4",
    audio: _url("T4"),
  },
  {
    id: "recap-phase4",
    type: "summary",
    kicker: "// RECAP · PHASE 4",
    title: "Every dollar and device accounted for",
    bullets: [
      "Clearing reports reconciled automatically",
      "Payouts and commissions in one tap",
      "Terminals shipped and activated",
      "Traffic routed to avoid declines",
    ],
    caption:
      "That's Phase 4. Every dollar reconciled, every agent paid, every terminal tracked, and every transaction routed to the right MID. The back office runs itself.",
    step: "S6",
    audio: _url("S6"),
  },
  {
    id: "phase5",
    target: "nds-accordion-phase5_veroogle",
    action: "open-phase5",
    kicker: "// 09 · PHASE 5 — AUTOMATION, ANALYTICS & AI",
    title: "Scale without adding headcount",
    caption:
      "Phase 5 is the force multiplier — a developer API, when-then workflow logic, churn warnings, and AI-powered support. This is where VEROOGLE scales without adding headcount.",
    step: "09",
    audio: _url("09"),
  },
  {
    id: "tool-api",
    target: "veroogle-card-t5300cr",
    kicker: "// PHASE 5 · TOOL 1 · T5300CR",
    title: "Developer API & Webhooks",
    caption:
      "Phase 5 is the force multiplier. The Developer API gives your team keys and webhooks, so every event in VEROOGLE can trigger anything else you run. Watch a live webhook fire and land.",
    action: "run:veroogle-t5300-run",
    step: "U1",
    audio: _url("U1"),
  },
  {
    id: "tool-workflow",
    target: "veroogle-card-t4480cr",
    kicker: "// PHASE 5 · TOOL 2 · T4480CR",
    title: "Workflow Logic",
    caption:
      "Workflow Logic lets you build when-then rules in plain language. When volume drops thirty percent, text the agent and open a retention task. And there it goes, triggered automatically.",
    action: "run:veroogle-t4480-run",
    step: "U2",
    audio: _url("U2"),
  },
  {
    id: "tool-churn",
    target: "veroogle-card-t6100cr",
    kicker: "// PHASE 5 · TOOL 3 · T6100CR",
    title: "Merchant Success & Churn Warning",
    caption:
      "Churn Warning spots merchants slipping before they leave. This coffee shop is down twenty-eight percent, VEROOGLE scores the risk, sends a retention offer, and the merchant stays.",
    action: "run:veroogle-t6100-run",
    step: "U3",
    audio: _url("U3"),
  },
  {
    id: "tool-ai",
    target: "veroogle-card-t6200cr",
    kicker: "// PHASE 5 · TOOL 4 · T6200CR",
    title: "Operations Support & AI Ticketing",
    caption:
      "And finally, AI Ticketing. A merchant's terminal won't connect. The AI diagnoses it, sends the fix, and closes the ticket in under a minute, no hold music required.",
    action: "run:veroogle-t6200-run",
    step: "U4",
    audio: _url("U4"),
  },
  {
    id: "recap-phase5",
    type: "summary",
    kicker: "// RECAP · PHASE 5",
    title: "Automation that scales you",
    bullets: [
      "API and webhooks connect everything",
      "When-then rules automate the busywork",
      "Churn caught before merchants leave",
      "AI resolves support in seconds",
    ],
    caption:
      "That's Phase 5. Your systems talk to each other, the busywork automates itself, churn is caught early, and support runs on AI. That's how VEROOGLE scales without adding headcount.",
    step: "S7",
    audio: _url("S7"),
  },
  {
    id: "phase6",
    target: "nds-accordion-phase6_veroogle",
    action: "open-phase6",
    kicker: "// 10 · PHASE 6 — CUSTOM SPECIALTY ENGINES",
    title: "Built for your special needs",
    caption:
      "Phase 6 is where VEROOGLE becomes truly yours. Every company has special needs, so this is where we build them: custom workflows, custom data, custom integrations, and custom reports, all made to fit exactly how you work.",
    step: "10",
    audio: _url("10"),
  },
  {
    id: "tool-custom-workflow",
    target: "veroogle-card-t7100cr",
    kicker: "// PHASE 6 · TOOL 1 · T7100CR",
    title: "Custom Workflow Builder",
    caption:
      "First, the Custom Workflow Builder. Snap a trigger and a few actions together, and a brand new process is live. Merchant approved, send the welcome kit, alert the agent, schedule the thirty day review. Built and tested in seconds.",
    action: "run:veroogle-t7100-run",
    step: "V1",
    audio: _url("V1"),
  },
  {
    id: "tool-custom-fields",
    target: "veroogle-card-t7200cr",
    kicker: "// PHASE 6 · TOOL 2 · T7200CR",
    title: "Custom Data Fields",
    caption:
      "Custom Data Fields. If your business tracks something nobody else does, we add it. Liquor license numbers, seasonal volume, franchise groups. Each field drops right into the merchant record. No code, no waiting.",
    action: "run:veroogle-t7200-run",
    step: "V2",
    audio: _url("V2"),
  },
  {
    id: "tool-custom-integration",
    target: "veroogle-card-t7300cr",
    kicker: "// PHASE 6 · TOOL 3 · T7300CR",
    title: "Custom Integration Connector",
    caption:
      "The Custom Integration Connector. That niche system you've been stuck with for years? We connect it. Authenticate, map the fields, and watch over a thousand records sync straight into VEROOGLE.",
    action: "run:veroogle-t7300-run",
    step: "V3",
    audio: _url("V3"),
  },
  {
    id: "tool-custom-report",
    target: "veroogle-card-t7400cr",
    kicker: "// PHASE 6 · TOOL 4 · T7400CR",
    title: "Custom Report Builder",
    caption:
      "And the Custom Report Builder. Pick the metrics that matter to you, choose how you want to see them, and the report builds itself, then lands in your inbox every Monday morning.",
    action: "run:veroogle-t7400-run",
    step: "V4",
    audio: _url("V4"),
  },
  {
    id: "recap-phase6",
    type: "summary",
    kicker: "// RECAP · PHASE 6",
    title: "Custom, for real",
    bullets: [
      "Your own workflows, snapped together",
      "Fields for whatever you track",
      "Niche systems connected and syncing",
      "Reports built your way, on schedule",
    ],
    caption:
      "That's Phase 6. Whatever makes VEROOGLE different, the workflow, the data, the systems, the reports, gets built right into your engine. That's what custom really means.",
    step: "S8",
    audio: _url("S8"),
  },
  {
    id: "recap-final",
    type: "summary",
    kicker: "// WRAP-UP · VEROOGLE END TO END",
    title: "One platform, six phases",
    bullets: [
      "Phase 1 — find and win the merchant",
      "Phase 2 — onboard and verify",
      "Phase 3 — protect your book",
      "Phase 4 — move the money and hardware",
      "Phase 5 — automate and scale",
      "Phase 6 — built for your special needs",
    ],
    caption:
      "And that's VEROOGLE end to end. Phase 1 finds and wins the merchant. Phase 2 onboards and verifies them. Phase 3 protects your book. Phase 4 keeps the money and the hardware moving. Phase 5 automates all of it. And Phase 6 builds in everything that makes you different, so you scale without adding headcount. Let's talk about getting you live.",
    cta: "book-call",
    step: "S3",
    audio: _url("S3"),
  },
];
